import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import { timeToMinutes, formatTimeDisplay, getHourOptions, getMinuteOptions, to12Hour, to24Hour } from '@/lib/timeUtils';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';

export interface TimeRangePickerProps {
  startTime?: string | null; // "HH:mm"
  endTime?: string | null; // "HH:mm"
  onChange?: (range: { start: string | null; end: string | null }) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  use24Hour?: boolean;
  minuteStep?: number; // default 5
  minTime?: string; // "HH:mm"
  maxTime?: string; // "HH:mm"
  className?: string;
}

interface TimeColumnProps {
  title: string;
  selectedHour: number | null; // 24h
  selectedMinute: number | null;
  period: 'AM' | 'PM';
  is24Hour: boolean;
  hours: number[];
  minutes: number[];
  onHourClick: (hour: number) => void;
  onMinuteClick: (minute: number) => void;
  onPeriodChange: (period: 'AM' | 'PM') => void;
  isTimeInRange: (h: number, m: number) => boolean;
}

function TimeColumn({
  title,
  selectedHour,
  selectedMinute,
  period,
  is24Hour,
  hours,
  minutes,
  onHourClick,
  onMinuteClick,
  onPeriodChange,
  isTimeInRange,
}: TimeColumnProps) {
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // Scroll selected items into view on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      hourListRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
      minuteListRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
    });
  }, []);

  return (
    <div className="flex-1 min-w-0">
      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground text-center border-b border-border">
        {title}
      </div>
      <div className="flex">
        {/* Hours */}
        <div
          ref={hourListRef}
          role="listbox"
          aria-label={`${title} hours`}
          className="max-h-48 overflow-y-auto border-r border-border py-1 scrollbar-thin flex-1"
        >
          {hours.map((hour) => {
            const h24 = is24Hour ? hour : to24Hour(hour, period);
            const hourDisabled = !minutes.some((m) => isTimeInRange(h24, m));
            const isSelected = is24Hour
              ? selectedHour === hour
              : selectedHour !== null && to12Hour(selectedHour) === hour;

            return (
              <button
                key={hour}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected}
                disabled={hourDisabled}
                onClick={() => onHourClick(hour)}
                className={cn(
                  'w-full px-2 py-1 text-sm text-center cursor-default select-none text-popover-foreground',
                  'hover:bg-primary-50 hover:text-primary-700',
                  'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                  isSelected && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white',
                  hourDisabled && 'opacity-40 pointer-events-none'
                )}
              >
                {is24Hour ? String(hour).padStart(2, '0') : String(hour)}
              </button>
            );
          })}
        </div>

        {/* Minutes */}
        <div
          ref={minuteListRef}
          role="listbox"
          aria-label={`${title} minutes`}
          className={cn(
            'max-h-48 overflow-y-auto py-1 scrollbar-thin flex-1',
            !is24Hour && 'border-r border-border'
          )}
        >
          {minutes.map((minute) => {
            const h24 = selectedHour ?? (is24Hour ? 0 : to24Hour(12, period));
            const minuteDisabled = !isTimeInRange(h24, minute);
            const isSelected = selectedMinute === minute;

            return (
              <button
                key={minute}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected}
                disabled={minuteDisabled}
                onClick={() => onMinuteClick(minute)}
                className={cn(
                  'w-full px-2 py-1 text-sm text-center cursor-default select-none text-popover-foreground',
                  'hover:bg-primary-50 hover:text-primary-700',
                  'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                  isSelected && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white',
                  minuteDisabled && 'opacity-40 pointer-events-none'
                )}
              >
                {String(minute).padStart(2, '0')}
              </button>
            );
          })}
        </div>

        {/* AM/PM toggle (12h mode only) */}
        {!is24Hour && (
          <div className="flex flex-col justify-center gap-1 px-1.5 py-1">
            {(['AM', 'PM'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => onPeriodChange(p)}
                className={cn(
                  'rounded-md px-1.5 py-1 text-xs font-medium cursor-default select-none text-popover-foreground',
                  'hover:bg-primary-50 hover:text-primary-700',
                  'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                  period === p && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TimeRangePicker({
  startTime,
  endTime,
  onChange,
  placeholder,
  label,
  error,
  hint,
  disabled,
  use24Hour,
  minuteStep = 5,
  minTime,
  maxTime,
  className,
}: TimeRangePickerProps) {
  const localeSettings = useLocaleSettings();
  const is24Hour = useMemo(
    () => (use24Hour !== undefined ? use24Hour : localeSettings.timeFormat !== 'hh:mm A'),
    [use24Hour, localeSettings.timeFormat]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [startPeriod, setStartPeriod] = useState<'AM' | 'PM'>('AM');
  const [endPeriod, setEndPeriod] = useState<'AM' | 'PM'>('PM');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = React.useId();

  // Parse values
  const startHour = startTime ? parseInt(startTime.split(':')[0], 10) : null;
  const startMinute = startTime ? parseInt(startTime.split(':')[1], 10) : null;
  const endHour = endTime ? parseInt(endTime.split(':')[0], 10) : null;
  const endMinute = endTime ? parseInt(endTime.split(':')[1], 10) : null;

  // Sync periods from values
  useEffect(() => {
    if (startHour !== null && !is24Hour) {
      setStartPeriod(startHour >= 12 ? 'PM' : 'AM');
    }
  }, [startHour, is24Hour]);

  useEffect(() => {
    if (endHour !== null && !is24Hour) {
      setEndPeriod(endHour >= 12 ? 'PM' : 'AM');
    }
  }, [endHour, is24Hour]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const hours = useMemo(() => getHourOptions(is24Hour), [is24Hour]);
  const minutes = useMemo(() => getMinuteOptions(minuteStep), [minuteStep]);

  const isTimeInRange = useCallback(
    (h: number, m: number): boolean => {
      const total = h * 60 + m;
      if (minTime && total < timeToMinutes(minTime)) return false;
      if (maxTime && total > timeToMinutes(maxTime)) return false;
      return true;
    },
    [minTime, maxTime]
  );

  const formatTime = (h24: number, m: number): string =>
    `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  // Start time handlers
  const handleStartHourClick = useCallback(
    (hour: number) => {
      const h24 = is24Hour ? hour : to24Hour(hour, startPeriod);
      const m = startMinute ?? 0;
      if (!isTimeInRange(h24, m)) return;
      onChange?.({ start: formatTime(h24, m), end: endTime ?? null });
    },
    [is24Hour, startPeriod, startMinute, endTime, onChange, isTimeInRange]
  );

  const handleStartMinuteClick = useCallback(
    (minute: number) => {
      const h24 = startHour ?? (is24Hour ? 0 : to24Hour(12, startPeriod));
      if (!isTimeInRange(h24, minute)) return;
      onChange?.({ start: formatTime(h24, minute), end: endTime ?? null });
    },
    [startHour, is24Hour, startPeriod, endTime, onChange, isTimeInRange]
  );

  const handleStartPeriodChange = useCallback(
    (newPeriod: 'AM' | 'PM') => {
      setStartPeriod(newPeriod);
      if (startHour === null) return;
      const currentH12 = to12Hour(startHour);
      const newH24 = to24Hour(currentH12, newPeriod);
      const m = startMinute ?? 0;
      if (!isTimeInRange(newH24, m)) return;
      onChange?.({ start: formatTime(newH24, m), end: endTime ?? null });
    },
    [startHour, startMinute, endTime, onChange, isTimeInRange]
  );

  // End time handlers
  const handleEndHourClick = useCallback(
    (hour: number) => {
      const h24 = is24Hour ? hour : to24Hour(hour, endPeriod);
      const m = endMinute ?? 0;
      if (!isTimeInRange(h24, m)) return;
      onChange?.({ start: startTime ?? null, end: formatTime(h24, m) });
    },
    [is24Hour, endPeriod, endMinute, startTime, onChange, isTimeInRange]
  );

  const handleEndMinuteClick = useCallback(
    (minute: number) => {
      const h24 = endHour ?? (is24Hour ? 0 : to24Hour(12, endPeriod));
      if (!isTimeInRange(h24, minute)) return;
      onChange?.({ start: startTime ?? null, end: formatTime(h24, minute) });
    },
    [endHour, is24Hour, endPeriod, startTime, onChange, isTimeInRange]
  );

  const handleEndPeriodChange = useCallback(
    (newPeriod: 'AM' | 'PM') => {
      setEndPeriod(newPeriod);
      if (endHour === null) return;
      const currentH12 = to12Hour(endHour);
      const newH24 = to24Hour(currentH12, newPeriod);
      const m = endMinute ?? 0;
      if (!isTimeInRange(newH24, m)) return;
      onChange?.({ start: startTime ?? null, end: formatTime(newH24, m) });
    },
    [endHour, endMinute, startTime, onChange, isTimeInRange]
  );

  const handleClear = useCallback(() => {
    onChange?.({ start: null, end: null });
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) setIsOpen((prev) => !prev);
      }
    },
    [disabled]
  );

  const hasError = Boolean(error);
  const hasValue = Boolean(startTime || endTime);

  const displayValue = useMemo(() => {
    if (!startTime && !endTime) return '';
    const startDisplay = startTime ? formatTimeDisplay(startTime, is24Hour) : '--:--';
    const endDisplay = endTime ? formatTimeDisplay(endTime, is24Hour) : '--:--';
    return `${startDisplay} \u2013 ${endDisplay}`;
  }, [startTime, endTime, is24Hour]);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        id={inputId}
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={hasError}
        aria-describedby={
          hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
        }
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError && 'border-error-500 focus:ring-error-500',
          !displayValue && 'text-muted-foreground'
        )}
      >
        <span>{displayValue || placeholder || 'Select time range'}</span>
        <Clock className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 mt-1 w-[420px] rounded-md border border-border bg-popover text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2'
          )}
        >
          <div className="flex divide-x divide-border">
            <TimeColumn
              title="From"
              selectedHour={startHour}
              selectedMinute={startMinute}
              period={startPeriod}
              is24Hour={is24Hour}
              hours={hours}
              minutes={minutes}
              onHourClick={handleStartHourClick}
              onMinuteClick={handleStartMinuteClick}
              onPeriodChange={handleStartPeriodChange}
              isTimeInRange={isTimeInRange}
            />
            <TimeColumn
              title="To"
              selectedHour={endHour}
              selectedMinute={endMinute}
              period={endPeriod}
              is24Hour={is24Hour}
              hours={hours}
              minutes={minutes}
              onHourClick={handleEndHourClick}
              onMinuteClick={handleEndMinuteClick}
              onPeriodChange={handleEndPeriodChange}
              isTimeInRange={isTimeInRange}
            />
          </div>

          {/* Footer with clear */}
          {hasValue && (
            <div className="border-t border-border px-2 py-1.5">
              <button
                type="button"
                onClick={handleClear}
                className="w-full rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-primary-50 hover:text-primary-700 text-center"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className="mt-1 text-sm text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}
